import React from "react";
import {
	SOCIAL_PLATFORMS,
	SocialPlatformKey,
} from "../../../constants/socialPlatforms";
import { FiUser, FiMail, FiPhone, FiGlobe, FiCreditCard, FiCheckCircle, FiLogIn, FiUserPlus } from "react-icons/fi";

interface Step3Props {
	formData: {
		organizerType: string;
		organizerName: string;
		representativeName: string;
		email: string;
		phone: string;
		website: string;
		description: string;
		bankName: string;
		branchName: string;
		accountType: string;
		accountNumber: string;
		accountHolderName: string;
		agreeToTerms: boolean;
	};
	socialLinks: Record<SocialPlatformKey, string>;
	handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	prevStep: () => void;
	isSubmitting?: boolean;
	onSubmit: (e: React.FormEvent) => void;
	isAuthenticated: boolean;
	onLoginRedirect: () => void;
	onRegisterRedirect: () => void;
}

const Step3: React.FC<Step3Props> = ({
	formData,
	socialLinks,
	handleChange,
	prevStep,
	isSubmitting = false,
	onSubmit,
	isAuthenticated,
	onLoginRedirect,
	onRegisterRedirect,
}) => {
	const filledSocialLinks = SOCIAL_PLATFORMS.filter(
		(platform) => socialLinks[platform.key]?.trim()
	);

	return (
		<form onSubmit={onSubmit} className="space-y-6">
			{/* Header */}
			<div className="text-center mb-8">
				<div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full mb-4">
					<FiCheckCircle className="w-8 h-8 text-white" />
				</div>
				<h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">入力内容の確認</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					内容をご確認いただき、問題なければ登録を完了してください
				</p>
			</div>

			{/* Basic Information Card */}
			<div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
				<div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4">
					<div className="flex items-center space-x-3">
						<div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-lg backdrop-blur-sm">
							<FiUser className="w-5 h-5 text-white" />
						</div>
						<h4 className="text-lg font-semibold text-white">基本情報</h4>
					</div>
				</div>
				<div className="p-6 space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiUser className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">主催者タイプ</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">
									{formData.organizerType === "artist"
										? "アーティスト"
										: formData.organizerType === "company"
										? "会社・団体"
										: "個人"}
								</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiUser className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">名称</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white break-all">{formData.organizerName}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiUser className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">代表者氏名</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.representativeName}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiMail className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">メールアドレス</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white break-all">{formData.email}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiPhone className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">電話番号</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.phone}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiGlobe className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">ウェブサイト</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white break-all">{formData.website || "-"}</p>
							</div>
						</div>
					</div>

					{formData.description && (
						<div className="pt-4 border-t border-gray-200 dark:border-gray-700">
							<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">プロフィール・説明</p>
							<p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{formData.description}</p>
						</div>
					)}

					{filledSocialLinks.length > 0 && (
						<div className="pt-4 border-t border-gray-200 dark:border-gray-700">
							<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">公式SNS</p>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								{filledSocialLinks.map((platform) => (
									<div
										key={platform.key}
										className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
									>
										<div className="text-sm font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">
											{platform.label}
										</div>
										<div className="text-sm text-gray-900 dark:text-white truncate flex-1">
											{socialLinks[platform.key]}
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Bank Account Information Card */}
			<div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
				<div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4">
					<div className="flex items-center space-x-3">
						<div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-lg backdrop-blur-sm">
							<FiCreditCard className="w-5 h-5 text-white" />
						</div>
						<h4 className="text-lg font-semibold text-white">振込先口座情報</h4>
					</div>
				</div>
				<div className="p-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiCreditCard className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">金融機関名</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.bankName || "-"}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiCreditCard className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">支店名</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.branchName || "-"}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiCreditCard className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">口座種別</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.accountType || "-"}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiCreditCard className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">口座番号</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.accountNumber || "-"}</p>
							</div>
						</div>
						<div className="flex items-start space-x-3 md:col-span-2">
							<div className="flex items-center justify-center w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
								<FiUser className="w-4 h-4 text-violet-600 dark:text-violet-400" />
							</div>
							<div>
								<p className="text-xs text-gray-500 dark:text-gray-400">口座名義人</p>
								<p className="text-sm font-medium text-gray-900 dark:text-white">{formData.accountHolderName || "-"}</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Terms Agreement */}
			<div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-xl border border-violet-200 dark:border-violet-800 p-6">
				<div className="flex items-start space-x-4">
					<div className="flex items-center h-5 relative mt-1">
						<input
							id="agree-checkbox"
							type="checkbox"
							name="agreeToTerms"
							checked={formData.agreeToTerms}
							onChange={handleChange}
							className="w-5 h-5 border-2 border-violet-300 dark:border-violet-600 rounded bg-white dark:bg-gray-700 focus:ring-3 focus:ring-violet-300 dark:focus:ring-violet-400 checked:bg-violet-600 checked:border-violet-600 appearance-none relative cursor-pointer"
						/>
						{formData.agreeToTerms && (
							<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
								<svg
									className="w-3 h-3 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="3"
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
						)}
					</div>
					<label
						htmlFor="agree-checkbox"
						className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
					>
						<a
							href="/terms-for-organizers"
							target="_blank"
							rel="noopener noreferrer"
							className="text-violet-600 dark:text-violet-400 hover:underline font-semibold"
						>
							主催者利用規約
						</a>
						に同意して登録します
					</label>
				</div>
			</div>

			{/* Auth Prompt (when not authenticated) */}
			{!isAuthenticated && (
				<div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
					<div className="text-center">
						<div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-3">
							<FiLogIn className="w-6 h-6 text-white" />
						</div>
						<h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
							アカウントが必要です
						</h4>
						<p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
							主催者登録を完了するには、ログインまたは新規アカウント登録が必要です。
							<br />
							<span className="text-blue-600 dark:text-blue-400 font-medium">
								入力した内容はすべて保存されます。
							</span>
						</p>
						<div className="flex flex-col sm:flex-row gap-3 justify-center">
							<button
								type="button"
								onClick={onLoginRedirect}
								className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
							>
								<FiLogIn className="w-5 h-5" />
								<span>ログインして登録する</span>
							</button>
							<button
								type="button"
								onClick={onRegisterRedirect}
								className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded-xl font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 hover:border-blue-400 dark:hover:border-blue-600 transition-all shadow-sm"
							>
								<FiUserPlus className="w-5 h-5" />
								<span>新規登録して登録する</span>
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Action Buttons */}
			<div className="flex flex-col sm:flex-row gap-4 pt-4">
				<button
					type="button"
					onClick={prevStep}
					className="flex-1 sm:flex-none sm:w-48 px-6 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all shadow-sm"
				>
					← 前のステップへ
				</button>
				{isAuthenticated && (
					<button
						type="submit"
						disabled={isSubmitting || !formData.agreeToTerms}
						className="flex-1 px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-bold hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none flex items-center justify-center space-x-2"
					>
						{isSubmitting ? (
							<>
								<div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
								<span>登録中...</span>
							</>
						) : (
							<>
								<FiCheckCircle className="w-5 h-5" />
								<span>主催者登録を完了する</span>
							</>
						)}
					</button>
				)}
			</div>
		</form>
	);
};

export default Step3;
